import { Component } from '@theme/component';

const DAY_MS = 86_400_000;
const TICK_COARSE_MS = 30_000;
const TICK_FINE_MS = 1_000;

/** Fire just past the boundary, never exactly on it — see #tick. */
const TICK_OVERSHOOT_MS = 20;

/** Above this many hours the wait is shown in days instead — „160 часа" stops being legible. */
const HOURS_TO_DAYS = 72;

/** Bulgarian weekday names, indexed by `Date.prototype.getUTCDay()`. */
const WEEKDAYS = ['неделя', 'понеделник', 'вторник', 'сряда', 'четвъртък', 'петък', 'събота'];

/** „в" becomes „във" before a word starting with в/ф — only вторник qualifies here. */
const PREPOSITIONS = ['в', 'в', 'във', 'в', 'в', 'в', 'в'];

/**
 * Bulgarian public holidays with a fixed date (Кодекс на труда, чл. 154, ал. 1), as [month, day].
 *
 * 1 ноември (Ден на народните будители) is deliberately absent: it is a non-attendance day for
 * schools and universities only, not a general non-working day — couriers and shops operate.
 */
const FIXED_HOLIDAYS = [
  [1, 1],
  [3, 3],
  [5, 1],
  [5, 6],
  [5, 24],
  [9, 6],
  [9, 22],
  [12, 24],
  [12, 25],
  [12, 26],
];

/** Reads the wall clock in Europe/Sofia regardless of where the visitor is. */
const SOFIA_CLOCK = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Sofia',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** @param {Date} date @returns {Record<string, number>} */
function sofiaParts(date) {
  const parts = SOFIA_CLOCK.formatToParts(date);
  /** @type {Record<string, number>} */
  const out = {};
  for (const part of parts) {
    if (part.type !== 'literal') out[part.type] = Number(part.value);
  }
  return out;
}

/**
 * Europe/Sofia's UTC offset at a given instant, in milliseconds.
 *
 * @param {number} epochMs
 * @returns {number}
 */
function sofiaOffsetMs(epochMs) {
  const p = sofiaParts(new Date(epochMs));
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - epochMs;
}

/**
 * Converts a Sofia wall-clock date/time to an absolute instant. Two passes so a DST
 * transition between the guess and the real offset cannot leave the result an hour out.
 *
 * @param {number} year @param {number} month @param {number} day @param {number} hour
 * @returns {number} epoch milliseconds
 */
function sofiaCivilToEpoch(year, month, day, hour) {
  const naive = Date.UTC(year, month - 1, day, hour);
  const first = naive - sofiaOffsetMs(naive);
  return naive - sofiaOffsetMs(first);
}

/** Orthodox Easter Sunday (Meeus's Julian algorithm, shifted to the Gregorian calendar). */
function orthodoxEaster(year) {
  const a = year % 4;
  const b = year % 7;
  const c = year % 19;
  const d = (19 * c + 15) % 30;
  const e = (2 * a + 4 * b - d + 34) % 7;
  const month = Math.floor((d + e + 114) / 31);
  const day = ((d + e + 114) % 31) + 1;
  // The Julian→Gregorian gap is 13 days for every year between 1900 and 2099.
  return Date.UTC(year, month - 1, day) + 13 * DAY_MS;
}

/** @param {number} dayMs @returns {string} `YYYY-MM-DD` */
function isoDay(dayMs) {
  return new Date(dayMs).toISOString().slice(0, 10);
}

/** @type {Map<number, Set<string>>} */
const holidayCache = new Map();

/**
 * Every non-working day in Bulgaria for a year: the fixed holidays, the four Easter days, and
 * the compensation days the labour code grants when a fixed holiday lands on a weekend —
 * „Когато официалните празници, с изключение на Великденските, съвпаднат със събота и/или
 * неделя, първият или първите два работни дни след тях са неприсъствени."
 *
 * Government-decreed bridge days are *not* derivable from any rule; they come from the block's
 * `holidays` setting instead.
 *
 * @param {number} year
 * @returns {Set<string>}
 */
function bulgarianHolidays(year) {
  const cached = holidayCache.get(year);
  if (cached) return cached;

  const fixed = FIXED_HOLIDAYS.map(([month, day]) => Date.UTC(year, month - 1, day));
  const easterSunday = orthodoxEaster(year);
  const easter = [easterSunday - 2 * DAY_MS, easterSunday - DAY_MS, easterSunday, easterSunday + DAY_MS];

  const days = new Set([...fixed, ...easter].map(isoDay));

  for (const holiday of [...fixed].sort((a, b) => a - b)) {
    const weekday = new Date(holiday).getUTCDay();
    if (weekday !== 0 && weekday !== 6) continue;

    let compensation = holiday + DAY_MS;
    while (
      new Date(compensation).getUTCDay() === 0 ||
      new Date(compensation).getUTCDay() === 6 ||
      days.has(isoDay(compensation))
    ) {
      compensation += DAY_MS;
    }
    days.add(isoDay(compensation));
  }

  holidayCache.set(year, days);
  return days;
}

/**
 * Live „your parcel is collectable in X" estimate for Еконт/Спиди office pickup.
 *
 * Renders **inside the USP row's popup**, not in the row itself: the row states the promise
 * („Доставка до 24 часа") and this explains what that means for the visitor right now. The popup
 * has room for a full sentence, which is why the estimate reads as prose here rather than the
 * two-line headline+detail it used until 2026-08-30.
 *
 * Mirrors the shipping policy: orders placed before the cut-off on a working day ship the same
 * day; everything else ships the next working day; a parcel is ready for collection on the
 * working day after dispatch, from the pickup hour.
 *
 * Day arithmetic runs on civil dates projected onto UTC midnight (so stepping a day is always
 * exactly 24 h), while the two countdowns are real instants converted through the Sofia offset
 * (so they stay exact across a DST transition).
 *
 * @typedef {object} LlDeliveryEtaRefs
 * @property {HTMLElement} [output] - Holds the sentence; server-rendered empty (see the block).
 *
 * @extends {Component<LlDeliveryEtaRefs>}
 */
export class LlDeliveryEta extends Component {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** Last rendered markup, so a tick that changes nothing does not touch the DOM. */
  #lastHtml = '';

  /** The popover this lives in, if any — the clock only runs while it is open. */
  /** @type {HTMLElement | null} */
  #popup = null;

  connectedCallback() {
    super.connectedCallback();

    this.#popup = this.closest('[popover]');
    this.#popup?.addEventListener('toggle', this.#handleToggle);

    // Render once either way, so an immediate open shows a correct sentence rather than a blank
    // callout; only keep the per-second timer alive while the popup is actually on screen.
    if (this.#isVisible()) this.#tick();
    else this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#popup?.removeEventListener('toggle', this.#handleToggle);
    this.#popup = null;
    this.#stop();
  }

  /** @param {Event} event */
  #handleToggle = (event) => {
    if (/** @type {ToggleEvent} */ (event).newState === 'open') {
      // Re-render immediately: while closed the sentence may have gone stale by hours.
      this.#stop();
      this.#tick();
    } else {
      this.#stop();
    }
  };

  #stop() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  /**
   * True when the estimate is on screen. A browser without `popover` support renders the popup's
   * contents inline, so treat an unusable `:popover-open` as visible rather than freezing the clock.
   *
   * @returns {boolean}
   */
  #isVisible() {
    if (!this.#popup) return true;
    try {
      return this.#popup.matches(':popover-open');
    } catch {
      return true;
    }
  }

  get #cutoffHour() {
    return Number(this.dataset.cutoffHour) || 16;
  }

  get #pickupHour() {
    return Number(this.dataset.pickupHour) || 9;
  }

  /** Government-decreed extra non-working days from the block setting. */
  get #extraHolidays() {
    return new Set(
      (this.dataset.holidays ?? '')
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    );
  }

  /**
   * @param {number} dayMs @param {Set<string>} extra
   * @returns {boolean}
   */
  #isWorkingDay(dayMs, extra) {
    const weekday = new Date(dayMs).getUTCDay();
    if (weekday === 0 || weekday === 6) return false;

    const iso = isoDay(dayMs);
    return !bulgarianHolidays(new Date(dayMs).getUTCFullYear()).has(iso) && !extra.has(iso);
  }

  /**
   * The first working day strictly after `dayMs`.
   *
   * @param {number} dayMs @param {Set<string>} extra
   * @returns {number}
   */
  #nextWorkingDay(dayMs, extra) {
    let candidate = dayMs + DAY_MS;

    // Bounded so a pathological holiday list can never spin forever.
    for (let step = 0; step < 40; step++) {
      if (this.#isWorkingDay(candidate, extra)) return candidate;
      candidate += DAY_MS;
    }
    return candidate;
  }

  /**
   * „утре" when the day is tomorrow, otherwise the weekday with its preposition.
   *
   * @param {number} dayMs @param {number} todayMs
   * @returns {string}
   */
  #dayLabel(dayMs, todayMs) {
    if (dayMs - todayMs === DAY_MS) return 'утре';

    const weekday = new Date(dayMs).getUTCDay();
    return `${PREPOSITIONS[weekday]} ${WEEKDAYS[weekday]}`;
  }

  /** Bulgarian counts a masculine noun with its бройна форма: 1 час, but 2/5/62 часа. */
  #hoursWord(hours) {
    return hours === 1 ? '1 час' : `${hours} часа`;
  }

  #minutesWord(minutes) {
    return minutes === 1 ? '1 минута' : `${minutes} минути`;
  }

  #secondsWord(seconds) {
    return seconds === 1 ? '1 секунда' : `${seconds} секунди`;
  }

  /**
   * The wait until collection. Hours by default — the usual wait is under three days, and
   * „62 часа" is easier to judge than „2 дни 14 часа". Past HOURS_TO_DAYS the hour count stops
   * being legible (a Christmas closure reaches ~160), so it switches to days.
   *
   * @param {number} ms
   * @returns {string}
   */
  #hoursOnly(ms) {
    const minutes = Math.max(0, Math.floor(ms / 60_000));
    const hours = Math.floor(minutes / 60);

    if (hours > HOURS_TO_DAYS) {
      // Always ≥ 3 whole days here, so „дни" never needs the singular „ден".
      const days = Math.floor(hours / 24);
      const rest = hours % 24;
      return rest ? `${days} дни и ${this.#hoursWord(rest)}` : `${days} дни`;
    }
    if (hours > 0) return this.#hoursWord(hours);
    if (minutes > 0) return this.#minutesWord(minutes);
    return 'по-малко от минута';
  }

  /**
   * The cut-off countdown as a ticking `HH:MM:SS` clock. Digits rather than words here: this is
   * the urgency cue, and it only ever runs within the same day, so hours never exceed 23.
   *
   * @param {number} ms
   * @returns {string}
   */
  #clock(ms) {
    const total = Math.max(0, Math.floor(ms / 1000));
    const pad = (value) => String(value).padStart(2, '0');

    return `${pad(Math.floor(total / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
  }

  #tick() {
    // #render returns the ms until the clock's next visible change, or null when no clock is
    // showing. Scheduling against that (rather than a flat 1000 ms) is what keeps the countdown
    // from dropping a second: a fixed interval drifts, and any timer jitter then skips a value.
    const next = this.#render();
    const delay = next === null ? TICK_COARSE_MS : next + TICK_OVERSHOOT_MS;
    this.#timer = setTimeout(() => this.#tick(), delay);
  }

  /**
   * @returns {number | null} ms until the clock's next visible change, or null when no clock
   * is displayed and a coarse tick will do.
   */
  #render() {
    const { output } = this.refs;
    if (!output) return null;

    const extra = this.#extraHolidays;
    const nowMs = Date.now();
    const now = sofiaParts(new Date(nowMs));
    const todayMs = Date.UTC(now.year, now.month - 1, now.day);

    const cutoffMs = sofiaCivilToEpoch(now.year, now.month, now.day, this.#cutoffHour);
    const shipsToday = this.#isWorkingDay(todayMs, extra) && nowMs < cutoffMs;

    const shipMs = shipsToday ? todayMs : this.#nextWorkingDay(todayMs, extra);
    const pickupMs = this.#nextWorkingDay(shipMs, extra);
    const pickupDate = new Date(pickupMs);
    const pickupAt = sofiaCivilToEpoch(
      pickupDate.getUTCFullYear(),
      pickupDate.getUTCMonth() + 1,
      pickupDate.getUTCDate(),
      this.#pickupHour
    );

    const wait = this.#hoursOnly(pickupAt - nowMs);
    const when = this.#dayLabel(pickupMs, todayMs);
    const waitHtml = `<strong class="ll-usp__eta-wait">${wait}</strong>`;

    // Both branches end on the same clause, so the visitor always reads the arrival day and the
    // wait in the same place; only the lead — urgency or plain fact — changes.
    const html = shipsToday
      ? `Поръчайте в следващите <strong class="ll-usp__eta-clock" data-clock></strong> и изпращаме пратката още днес. ` +
        `Ще можете да я вземете от Еконт или Спиди <strong>${when}</strong> — след около ${waitHtml}.`
      : `Ако поръчате сега, изпращаме пратката <strong>${this.#dayLabel(shipMs, todayMs)}</strong>. ` +
        `Ще можете да я вземете от Еконт или Спиди <strong>${when}</strong> — след около ${waitHtml}.`;

    // Everything except the clock is rewritten only when it actually changes, so the per-second
    // tick touches a single text node instead of re-parsing the row every time.
    if (html !== this.#lastHtml) {
      this.#lastHtml = html;
      output.innerHTML = html;
    }

    if (!shipsToday) return null;

    const remaining = cutoffMs - nowMs;
    const clock = output.querySelector('[data-clock]');
    if (clock) clock.textContent = this.#clock(remaining);

    return remaining % 1000 || 1000;
  }
}

if (!customElements.get('ll-delivery-eta')) {
  customElements.define('ll-delivery-eta', LlDeliveryEta);
}
